import React from 'react'
import { Editor, type BeforeMount } from '@monaco-editor/react'
import { Maximize2, Minimize2, Braces, Copy, Check, Minimize } from 'lucide-react'
import styles from './JsonEditor.module.css'
import { safeJsonParse, safeJsonStringify, sanitizeJsonInput, MAX_LENGTHS } from '../../utils/security'
import { toast } from '../../lib/toast'
import { createPortal } from 'react-dom'

interface JsonEditorProps {
  value: string
  onChange: (value: string) => void
  height?: string
  readOnly?: boolean
  language?: string
  title?: string
}

export default function JsonEditor({
  value,
  onChange,
  height = '300px',
  readOnly = false,
  language = 'json',
  title = 'JSON Editor',
}: JsonEditorProps) {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const [tempValue, setTempValue] = React.useState(value)
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    setTempValue(value)
  }, [value])

  const getCurrentValue = () => isExpanded ? tempValue : value

  const setCurrentValue = (val: string) => {
    if (isExpanded) {
      setTempValue(val)
    } else {
      onChange(val)
    }
  }

  React.useEffect(() => {
  if (!isExpanded) return
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') handleCancel()
  }
  document.addEventListener('keydown', onKeyDown)
  const prevOverflow = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  return () => {
    document.removeEventListener('keydown', onKeyDown)
    document.body.style.overflow = prevOverflow
   }
  }, [isExpanded])

  const handleEditorChange = (newValue: string | undefined) => {
    const val = newValue || ''
    const sanitized = sanitizeJsonInput(val)
    if (sanitized.length > MAX_LENGTHS.json) {
      return
    }
    if (isExpanded) {
      setTempValue(sanitized)
    } else {
      onChange(sanitized)
    }
  }

  const prepareForParse = (input: string) => {
    const placeholders: Record<string, string> = {}
    let idx = 0
    // Replace "{{...}}" (quoted) → "__TPL_N__" and standalone {{...}} → "__TPL_N__"
    const replaced = input.replace(/"(\{\{[^}]*\}\})"/g, (_match, token) => {
      const key = `"__TPL_${idx}__"`
      placeholders[`__TPL_${idx}__`] = token
      idx++
      return key
    }).replace(/\{\{([^}]*)\}\}/g, (_match, inner) => {
      const token = `{{${inner}}}`
      const key = `"__TPL_${idx}__"`
      placeholders[`__TPL_${idx}__`] = token
      idx++
      return key
    })
    return { replaced, placeholders }
  }

  const restorePlaceholders = (output: string, placeholders: Record<string, string>) => {
    let result = output
    for (const [key, original] of Object.entries(placeholders)) {
      result = result.replace(key, original)
    }
    return result
  }

  const formatJson = () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    const { replaced, placeholders } = prepareForParse(current)
    const result = safeJsonParse(replaced, {})
    if (result.success) {
      const formatted = restorePlaceholders(safeJsonStringify(result.data, '{}'), placeholders)
      setCurrentValue(formatted)
      toast.success('JSON formatted')
    } else {
      toast.error(`Invalid JSON: ${result.error}`)
    }
  }

  const minifyJson = () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    const { replaced, placeholders } = prepareForParse(current)
    const result = safeJsonParse(replaced, {})
    if (result.success) {
      try {
        const minified = restorePlaceholders(JSON.stringify(result.data), placeholders)
        setCurrentValue(minified)
        toast.success('JSON minified')
      } catch {
        toast.error('Failed to minify JSON')
      }
    } else {
      toast.error(`Invalid JSON: ${result.error}`)
    }
  }

  const copyToClipboard = async () => {
    const current = getCurrentValue()
    if (!current.trim()) return
    try {
      await navigator.clipboard.writeText(current)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }

  const handleExpand = () => {
    setTempValue(value)
    setIsExpanded(true)
  }

  const handleCollapse = () => {
    onChange(tempValue)
    setIsExpanded(false)
  }

  const handleCancel = () => {
    setTempValue(value)
    setIsExpanded(false)
  }

  const handleBeforeMount: BeforeMount = (monaco) => {
    monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: false,
      schemaValidation: 'ignore',
      enableSchemaRequest: false,
      allowComments: true,
      trailingCommas: 'ignore',
    })
  }

  const editorOptions = {
    readOnly,
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on' as const,
    lineNumbers: 'on' as const,
    folding: true,
    bracketPairColorization: { enabled: true },
    autoIndent: 'full' as const,
    formatOnPaste: true,
    overviewRulerLanes: 0,
    renderValidationDecorations: 'off' as const,
  }

  const actionButtons = (
    <div className={styles.actionButtons}>
      {!readOnly && language === 'json' && (
        <>
          <button
            type="button"
            onClick={formatJson}
            className={styles.actionBtn}
            title="Format JSON (prettify)"
          >
            <Braces size={13} />
            Format
          </button>
          <button
            type="button"
            onClick={minifyJson}
            className={styles.actionBtn}
            title="Minify JSON"
          >
            <Minimize size={13} />
            Minify
          </button>
        </>
      )}
      <button
        type="button"
        onClick={copyToClipboard}
        className={`${styles.actionBtn} ${copied ? styles.actionBtnSuccess : ''}`}
        title="Copy to clipboard"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  )

  return (
    <>
      <div className={styles.container}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            {actionButtons}
          </div>
          <div className={styles.toolbarRight}>
            <span className={styles.editorType}>
              <Braces size={14} />
              JSON
            </span>
            <button
              type="button"
              onClick={handleExpand}
              className={styles.expandBtn}
              title="Expand editor"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>

        <div className={styles.editorWrapper}>
          <Editor
            height={height}
            language={language}
            value={isExpanded ? tempValue : value}
            onChange={handleEditorChange}
            beforeMount={handleBeforeMount}
            options={editorOptions}
            theme="vs"
          />
        </div>
      </div>

      {isExpanded && createPortal(
        <div className={styles.modal}>
          <div className={styles.modalOverlay} onClick={handleCancel} />
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>
                <Braces size={18} />
                {title}
              </h3>
              <div className={styles.modalActions}>
                {actionButtons}
                <div className={styles.modalControls}>
                  <button
                    type="button"
                    onClick={handleCollapse}
                    className={styles.saveBtn}
                  >
                    Save & Close
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    className={styles.cancelBtn}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCollapse}
                    className={styles.collapseBtn}
                    title="Collapse editor"
                  >
                    <Minimize2 size={16} />
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalEditor}>
              <Editor
                height="calc(100vh - 120px)"
                language={language}
                value={tempValue}
                onChange={handleEditorChange}
                beforeMount={handleBeforeMount}
                options={{
                  ...editorOptions,
                  minimap: { enabled: true },
                  fontSize: 14,
                }}
                theme="vs"
              />
            </div>
          </div>
        </div>,
          document.body
      )}
    </>
  )
}
